import React from 'react'
import { ArrowRightEndOnRectangleIcon, CalendarIcon } from '@heroicons/react/24/outline'
import {
  DevicePhoneMobileIcon,
  MicrophoneIcon,
  PlayCircleIcon,
  PauseCircleIcon,
  QuestionMarkCircleIcon,
  XCircleIcon,
} from '@heroicons/react/24/solid'
import { IconButton } from '~/components/IconButton'
import { CallBotLayoutInfo, ServiceLogo, CallBotAction } from './types'
import { Dropdown } from '~/components/Dropdown'
import { JumpSpinner } from '~/components/JumpSpinner'
import { MeetingTitle, RecordingDateTime } from '../shared'
import { Tooltip } from '~/components/Tooltip'
import { Dispatch } from './callBot'

export function CallBotSidebarView({
  layoutInfo,
  canJoinMyCall,
  canUseWebRecorder,
  recordingInProgress,
  dispatch,
  removeMargins = false,
}: {
  layoutInfo: CallBotLayoutInfo
  canJoinMyCall: boolean
  canUseWebRecorder: boolean
  recordingInProgress: boolean
  dispatch: Dispatch
  removeMargins: boolean
}) {
  const url = layoutInfo.event_integration_reference_id
    ? `/prepare/${layoutInfo.event_integration_reference_id}`
    : undefined

  const handleButtonClick = (buttonType: CallBotAction) => {
    dispatch({
      action: 'button-click',
      buttonType,
      id: layoutInfo.id,
      url: layoutInfo.join_url,
      eventIntegrationReferenceId: layoutInfo.event_integration_reference_id,
    })
  }

  const v2 = window.jump.layoutV2Enabled

  return (
    <div
      className={(() => {
        let baseStyles = `flex flex-col gap-2 items-stretch flex flex-col text-sm ${removeMargins ? '' : 'mx-7'} border border-secondary rounded-md`

        if (!v2) {
          baseStyles += ' p-3'
        }

        if (v2) {
          baseStyles += ' p-2 flex-wrap max-h-[6em]'
        }

        return baseStyles
      })()}
      data-qa="call-bot"
    >
      <StatusRow layoutInfo={layoutInfo} />

      <div className="flex items-center">
        {layoutInfo.join_url ? (
          <a href={layoutInfo.join_url} target="_blank" rel="noopener noreferrer">
            <ServiceLogoIcon service_logo={layoutInfo.service_logo} />
          </a>
        ) : (
          <ServiceLogoIcon service_logo={layoutInfo.service_logo} />
        )}

        <div className="flex items-center gap-x-1 ml-2 grow justify-end">
          {layoutInfo.buttons?.map((buttonType) => (
            <ActionButton
              key={buttonType}
              type={buttonType}
              onClick={() => handleButtonClick(buttonType)}
              canJoinMyCall={canJoinMyCall}
              canUseWebRecorder={canUseWebRecorder}
              recordingInProgress={recordingInProgress}
              layoutInfo={layoutInfo}
            />
          ))}
        </div>
      </div>

      {!v2 ? (
        <div>
          <MeetingTitle title={layoutInfo.title || 'Untitled'} href={url} />

          <RecordingDateTime startedAtUnixMs={layoutInfo.timestampUnixMs} />
        </div>
      ) : null}
    </div>
  )
}

function StatusRow({ layoutInfo }: { layoutInfo: CallBotLayoutInfo }) {
  const statusIndicator = (
    <StatusIndicator type={layoutInfo.status_indicator} questionMarkTooltip={layoutInfo.question_mark_tooltip} />
  )

  return (
    <div className="flex items-center text-gray-500 gap-x-1" data-qa="call-bot-status-row">
      {statusIndicator}
      {layoutInfo.status}
      {!statusIndicator && layoutInfo.question_mark_tooltip && (
        <Tooltip tooltip={layoutInfo.question_mark_tooltip} position="top-right">
          <QuestionMarkCircleIcon className="h-4 text-gray-300" />
        </Tooltip>
      )}
    </div>
  )
}

function ServiceLogoIcon({ service_logo }: { service_logo: ServiceLogo }) {
  return <img className={`${service_logo.className} shrink-0`} src={service_logo.path} />
}

function StatusIndicator({
  type,
  questionMarkTooltip,
}: {
  type: 'moving' | 'idle' | 'recording' | 'error' | 'no_join_url'
  questionMarkTooltip?: string
}) {
  switch (type) {
    case 'idle':
      return <div className="h-2 w-2 rounded-[4px] bg-tertiary-inverse" />
    case 'recording':
      return <div className="h-2 w-2 rounded-[4px] animate-pulse bg-danger-inverse" />
    case 'error':
      return <div className="h-2 w-2 rounded-[4px] bg-warning-inverse" />
    case 'moving':
      return <JumpSpinner />
    default:
      return (
        questionMarkTooltip && (
          <Tooltip tooltip={questionMarkTooltip} position="top-right">
            <QuestionMarkCircleIcon className="h-4 text-gray-500" />
          </Tooltip>
        )
      )
  }
}

function ActionButton({
  type,
  onClick,
  canJoinMyCall,
  canUseWebRecorder,
  recordingInProgress,
  layoutInfo,
}: {
  type: CallBotAction
  onClick: () => void
  canJoinMyCall: boolean
  canUseWebRecorder: boolean
  recordingInProgress: boolean
  layoutInfo: CallBotLayoutInfo
}): React.ReactElement {
  switch (type) {
    case 'send_notetaker':
      return (
        <IconButton
          screenReaderText="Send notetaker"
          tooltip={
            layoutInfo.service_blocked_by_account_policy
              ? 'This meeting service is blocked by account policy'
              : 'Send notetaker'
          }
          icon={<ArrowRightEndOnRectangleIcon />}
          onClick={onClick}
          size="sm"
          variant="just-icon"
          disabled={layoutInfo.service_blocked_by_account_policy}
        />
      )

    case 'stop_notetaker':
      return (
        <IconButton
          screenReaderText="Remove notetaker"
          tooltip="Remove notetaker"
          icon={<XCircleIcon className="h-6 icon-danger-inverse" />}
          onClick={onClick}
          variant="just-icon"
        />
      )

    case 'resume_notetaker':
      return (
        <IconButton
          screenReaderText="Resume notetaker"
          tooltip="Resume notetaker"
          icon={<PlayCircleIcon className="h-6 icon-success" />}
          onClick={onClick}
          variant="just-icon"
        />
      )

    case 'pause_notetaker':
      return (
        <IconButton
          screenReaderText="Pause notetaker"
          tooltip="Pause notetaker"
          icon={<PauseCircleIcon className="h-6 icon-secondary" />}
          onClick={onClick}
          variant="just-icon"
        />
      )

    case 'send_ad_hoc_notetaker':
      return (
        <IconButton
          screenReaderText="Send new notetaker"
          tooltip="Send new notetaker"
          icon={<ArrowRightEndOnRectangleIcon />}
          onClick={onClick}
          size="sm"
        />
      )

    case 'join_my_meeting':
      return (
        <Dropdown
          align="start"
          trigger={
            <div className="flex items-center w-full py-1 px-4 rounded-md bg-success-inverse text-primary-inverse font-medium">
              <CalendarIcon className="h-6 mr-1.5" />
              <span>Join my meeting</span>
            </div>
          }
          items={[
            canUseWebRecorder && {
              icon: <MicrophoneIcon className="h-6 mr-1.5" />,
              text: 'Capture meeting now',
              description: 'In-person meeting or dictation',
              href: '/meetings/new?capture_now=true',
              disabled: recordingInProgress,
              tooltip: recordingInProgress
                ? 'To capture a new meeting, first submit or discard the current one.'
                : undefined,
            },
            {
              icon: <ArrowRightEndOnRectangleIcon className="h-6 mr-1.5" />,
              text: 'Send notetaker to meeting',
              description: 'Add meeting url',
              href: '/meetings/send-to-meeting',
            },
            canJoinMyCall && {
              icon: <DevicePhoneMobileIcon className="h-6 mr-1.5" />,
              text: 'Join my call',
              description: 'Get Jump to join a phone call',
              onClick,
            },
          ].filter((item) => !!item)}
        />
      )

    case 'in_twilio_call_recording':
      return <span>In meeting...</span>

    case 'completed_twilio_call_recording':
      return <span>Processing meeting...</span>
  }
}
